import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import useUser from '../../hooks/useUser';
import {
  loadCommission,
  loadGeneral,
  loadOptions,
  updateGeneralPayment,
  updateLogoUrl,
} from '../../features/labor/paymentSlice';
import { updateLabor } from '../../features/labor/laborSlice';
import SavedPricingOptions from '../pricing/SavedPricingOptions';

const COMPANY_FIELDS = [
  { field: 'companyAddressLineOne', label: 'Address Line 1' },
  { field: 'companyAddressLineTwo', label: 'Address Line 2' },
  { field: 'companyPhoneNumber', label: 'Phone Number' },
  { field: 'companyEmail', label: 'Email' },
];

// Matches DEFAULT_NEXT_STEPS in the backend's signingController, which the
// customer sees until the company writes its own.
const DEFAULT_NEXT_STEPS = [
  "We'll contact you shortly to confirm the details and schedule your project.",
  'Keep the signed copy for your records.',
  'Questions? Call or email us anytime.',
];
const MAX_NEXT_STEPS = 8;

const LOGO_MAX_SIDE = 600;
const LOGO_MAX_BYTES = 1024 * 1024;
const LIBRARY_MAX_BYTES = 25 * 1024 * 1024;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const formatFileSize = bytes =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`;

const readImageSize = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      reject(new Error('Unable to read image.'));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });

/**
 * One part of the company's "general" settings: `section="company"` for the
 * company details (Settings > Company), `section="estimates"` for the
 * after-signing steps, attachment library and the company's saved custom
 * options (Settings > Estimates; the options save on their own). Pricing
 * defaults, the rest of this section, are in Labor > Payment > General.
 * It loads the company's settings itself, so saving here never overwrites
 * them with empty values when the Labor page hasn't been opened.
 */
const GeneralSettings = ({ section }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { user } = useUser();
  const payment = useSelector(state => state.payment);
  const general = payment.general;

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const logoInputRef = useRef(null);
  const [logoPreview, setLogoPreview] = useState(payment.logoUrl || null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);

  const libraryInputRef = useRef(null);
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryUploading, setLibraryUploading] = useState(false);
  const [libraryBusyId, setLibraryBusyId] = useState(null);

  const notify = (title, status = 'error', description) =>
    toast({ title, description, status, duration: 4000, isClosable: true });

  const request = async (method, path, data, params) => {
    const token = await user.getIdToken();
    const response = await axios({
      method,
      url: `${process.env.REACT_APP_BACKEND_URL}${path}`,
      data,
      params,
      headers: { authorization: token },
    });
    return response.data;
  };

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const [labor, library] = await Promise.all([
          request('get', '/api/labors', undefined, { username: user.email }),
          section === 'estimates'
            ? request('get', '/api/pdf/library', undefined, {
                username: user.email,
              })
            : [],
        ]);
        // The Save below sends the whole payment object, so load all of it.
        if (labor?.payment?.options) dispatch(loadOptions(labor.payment.options));
        if (labor?.payment?.general) dispatch(loadGeneral(labor.payment.general));
        if (labor?.payment?.commission) {
          dispatch(loadCommission(labor.payment.commission));
        }
        if (labor?.hasBeenSaved) {
          dispatch(updateLabor({ hasBeenSaved: labor.hasBeenSaved }));
        }
        dispatch(updateLogoUrl(labor?.logoUrl || ''));
        setLogoPreview(labor?.logoUrl || null);
        setLibraryItems(library || []);
      } catch (error) {
        console.error('Could not load general settings:', error);
        notify('Could not load your settings', 'error', 'Please refresh.');
      }
      setIsLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setField = (field, value) => {
    setUnsavedChanges(true);
    dispatch(updateGeneralPayment({ newValue: value, fieldToUpdate: field }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // No tab: saving here doesn't mark anything reviewed in Labor (the
      // pricing defaults saved alongside are reviewed there).
      await request('post', '/api/labors/payment', {
        username: user.email,
        payment,
      });
      setUnsavedChanges(false);
      notify('Changes saved', 'success');
    } catch (error) {
      console.error(error);
      notify('Could not save', 'error', 'Please try again.');
    }
    setIsSaving(false);
  };

  // ---------------------------------------------------------------- logo

  const handleLogoUpload = async e => {
    const file = e.target.files?.[0];
    // Reset so choosing the same file again still triggers onChange.
    e.target.value = '';
    if (!file) return;

    const fail = description => notify('Logo not uploaded', 'error', description);
    if (!file.type.startsWith('image/')) {
      fail('Please select an image (PNG or JPEG).');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      fail('Logo must be under 1 MB.');
      return;
    }
    try {
      const { width, height } = await readImageSize(file);
      if (width > LOGO_MAX_SIDE || height > LOGO_MAX_SIDE) {
        fail(`Logo must be no larger than ${LOGO_MAX_SIDE} × ${LOGO_MAX_SIDE} pixels.`);
        return;
      }
    } catch {
      fail('That image could not be read.');
      return;
    }

    setIsLogoUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('username', user.email);
      const result = await request('post', '/api/prices/logo', form);
      dispatch(
        updateGeneralPayment({
          newValue: result.key,
          fieldToUpdate: 'companyLogoKey',
        })
      );
      // A new logo resets the accent to follow it (saved by the server).
      dispatch(
        updateGeneralPayment({
          newValue: result.logoColor || '',
          fieldToUpdate: 'companyLogoColor',
        })
      );
      dispatch(updateGeneralPayment({ newValue: '', fieldToUpdate: 'companyColor' }));
      dispatch(updateLogoUrl(result.url));
      setLogoPreview(result.url);
      notify('Logo uploaded', 'success');
    } catch (error) {
      console.error(error);
      fail(error.response?.data?.message || 'Please try again in a moment.');
    }
    setIsLogoUploading(false);
  };

  // Swatch shows what the PDF will use: the custom color, else the color
  // detected from the logo, else the default navy.
  const accentSwatch = HEX_COLOR.test(general.companyColor || '')
    ? general.companyColor
    : HEX_COLOR.test(general.companyLogoColor || '')
    ? general.companyLogoColor
    : '#1f3a5f';

  // Saved right away (not with Save) so it can't be lost.
  const handleUseLogoColor = async () => {
    try {
      await request('post', '/api/prices/company-color', {
        username: user.email,
        companyColor: '',
      });
      dispatch(updateGeneralPayment({ newValue: '', fieldToUpdate: 'companyColor' }));
    } catch (error) {
      console.error(error);
      notify('Could not switch to the logo color', 'error', 'Please try again.');
    }
  };

  // ---------------------------------------------------------------- library

  const handleLibraryUpload = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      notify('Please choose a PDF');
      return;
    }
    if (file.size > LIBRARY_MAX_BYTES) {
      notify('File is too large', 'error', 'Library PDFs must be under 25 MB.');
      return;
    }

    setLibraryUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('username', user.email);
      const item = await request('post', '/api/pdf/library', form);
      setLibraryItems(items => [item, ...items]);
    } catch (error) {
      console.error('Library upload failed:', error);
      notify('Upload failed', 'error', error.response?.data?.message || 'Please try again.');
    }
    setLibraryUploading(false);
  };

  const handleLibraryDelete = async item => {
    if (
      !window.confirm(
        `Delete "${item.name}" from the library? Estimates that already use it keep their copy.`
      )
    ) {
      return;
    }
    setLibraryBusyId(item.id);
    try {
      await request('post', '/api/pdf/library/delete', {
        username: user.email,
        id: item.id,
      });
      setLibraryItems(items => items.filter(i => i.id !== item.id));
    } catch (error) {
      console.error('Library delete failed:', error);
      notify('Could not delete', 'error', 'Please try again.');
    }
    setLibraryBusyId(null);
  };

  // ---------------------------------------------------------------- render

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner />
      </Center>
    );
  }

  // Next steps are stored as one line per step; unset means the defaults.
  const customSteps = general.signedNextSteps != null;
  const steps = customSteps
    ? general.signedNextSteps.split('\n')
    : DEFAULT_NEXT_STEPS;
  const setSteps = next => setField('signedNextSteps', next.join('\n'));
  const hasEmptyStep = steps.some(step => !step.trim());

  const textRow = ({ field, label }) => (
    <Tr key={field}>
      <Td>
        <Text>{label}</Text>
      </Td>
      <Td>
        <Input
          size="sm"
          maxWidth="450px"
          value={general[field] ?? ''}
          onChange={event => setField(field, event.target.value)}
        />
      </Td>
    </Tr>
  );

  const saveButton = (
    <Center>
      <VStack>
        {unsavedChanges && (
          <Alert status="warning">
            <AlertIcon />
            <AlertTitle>You have unsaved changes!</AlertTitle>
          </Alert>
        )}
        <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving}>
          Save
        </Button>
      </VStack>
    </Center>
  );

  if (section === 'company') {
    return (
      <VStack align="stretch" spacing={6}>
        <Box>
          <Table size="sm">
            <Tbody>
              <Tr>
                <Td>
                  <Text>User Name</Text>
                </Td>
                <Td>
                  <Text>{user?.email}</Text>
                </Td>
              </Tr>
              {textRow({ field: 'companyName', label: 'Company Name' })}
              <Tr>
                <Td>
                  <Text>Company Logo</Text>
                </Td>
                <Td>
                  <HStack spacing={4}>
                    <Box
                      w="150px"
                      h="100px"
                      borderWidth="1px"
                      borderRadius="md"
                      overflow="hidden"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bg="gray.50"
                    >
                      {logoPreview ? (
                        <Image
                          src={logoPreview}
                          alt="Logo"
                          maxW="100%"
                          maxH="100%"
                          objectFit="contain"
                        />
                      ) : (
                        <Text color="gray.500">No logo</Text>
                      )}
                    </Box>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                    />
                    <Button
                      size="sm"
                      onClick={() => logoInputRef.current.click()}
                      isLoading={isLogoUploading}
                    >
                      <Text fontSize="xs">{logoPreview ? 'Replace' : 'Upload'}</Text>
                    </Button>
                  </HStack>
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <Text>Estimate Accent Color</Text>
                </Td>
                <Td>
                  <HStack spacing={3}>
                    <Input
                      type="color"
                      size="sm"
                      width="56px"
                      padding="2px"
                      value={accentSwatch}
                      onChange={event => setField('companyColor', event.target.value)}
                    />
                    <Text fontSize="xs" color="gray.600">
                      {general.companyColor
                        ? `Custom ${general.companyColor.toUpperCase()}`
                        : general.companyLogoColor
                        ? `Matches your logo (${general.companyLogoColor.toUpperCase()})`
                        : 'Default navy'}
                    </Text>
                    {general.companyColor && general.companyLogoColor && (
                      <Button size="xs" variant="ghost" onClick={handleUseLogoColor}>
                        Use logo color
                      </Button>
                    )}
                  </HStack>
                </Td>
              </Tr>
              {COMPANY_FIELDS.map(textRow)}
            </Tbody>
          </Table>
        </Box>
        {saveButton}
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={10} pt={4} pb={10}>
      <Box>
        <HStack justify="space-between" mb={3}>
          <Heading size="sm">Attachment Library</Heading>
          <input
            ref={libraryInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleLibraryUpload}
            style={{ display: 'none' }}
          />
          <Button
            size="sm"
            leftIcon={<AddIcon boxSize={2.5} />}
            onClick={() => libraryInputRef.current.click()}
            isLoading={libraryUploading}
          >
            Upload to Library
          </Button>
        </HStack>
        {libraryItems.length === 0 ? (
          <Text fontSize="xs" color="gray.500">
            No stored attachments
          </Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {libraryItems.map(item => (
              <HStack
                key={item.id}
                spacing={3}
                p={2}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="white"
                opacity={libraryBusyId === item.id ? 0.6 : 1}
              >
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" noOfLines={1}>
                    {item.name}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {formatFileSize(item.size)}
                    {item.createdAt &&
                      ` · added ${new Date(item.createdAt).toLocaleDateString()}`}
                  </Text>
                </Box>
                <IconButton
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  icon={<DeleteIcon />}
                  aria-label={`Delete ${item.name} from library`}
                  onClick={() => handleLibraryDelete(item)}
                  isDisabled={libraryBusyId === item.id}
                />
              </HStack>
            ))}
          </VStack>
        )}
      </Box>

      <Box>
        <Heading size="sm" mb={3}>
          Custom Options
        </Heading>
        <SavedPricingOptions />
      </Box>

      <Box>
        <Heading size="sm" mb={3}>
          After Signing
        </Heading>
        <Box
          bg="white"
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="sm"
          p={5}
        >
          <HStack justify="space-between" mb={3}>
            <Heading size="md">What happens next</Heading>
            {customSteps && (
              <Button
                size="xs"
                variant="link"
                onClick={() => setField('signedNextSteps', null)}
              >
                Reset to default
              </Button>
            )}
          </HStack>
          <VStack align="stretch" spacing={2}>
            {steps.map((step, index) => (
              <HStack key={index} spacing={3}>
                <Center
                  flexShrink={0}
                  w="28px"
                  h="28px"
                  borderRadius="full"
                  bg="blue.50"
                  color="blue.700"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  {index + 1}
                </Center>
                <Input
                  variant="flushed"
                  value={step}
                  placeholder="Describe this step"
                  onChange={event =>
                    setSteps(
                      steps.map((s, i) => (i === index ? event.target.value : s))
                    )
                  }
                />
                <IconButton
                  size="sm"
                  variant="ghost"
                  icon={<DeleteIcon />}
                  aria-label={`Remove step ${index + 1}`}
                  onClick={() => setSteps(steps.filter((_, i) => i !== index))}
                  isDisabled={steps.length === 1}
                />
              </HStack>
            ))}
          </VStack>
          <HStack justify="space-between" mt={3}>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<AddIcon boxSize={2} />}
              onClick={() => setSteps([...steps, ''])}
              isDisabled={steps.length >= MAX_NEXT_STEPS}
            >
              Add step
            </Button>
            <HStack spacing={3}>
              {hasEmptyStep ? (
                <Text fontSize="sm" color="red.500">
                  Fill in or remove empty steps
                </Text>
              ) : (
                unsavedChanges && (
                  <Text fontSize="sm" color="orange.500">
                    Unsaved changes
                  </Text>
                )
              )}
              <Button
                colorScheme="blue"
                size="sm"
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={hasEmptyStep}
              >
                Save
              </Button>
            </HStack>
          </HStack>
        </Box>
      </Box>
    </VStack>
  );
};

export default GeneralSettings;
